import { useEffect } from 'react';
import { Modal, Stack, MultiSelect, Textarea, Button, Group, Checkbox, Text, Table } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { coachesApi } from '../../../services/api/coaches';
import type { Coach, UpdateCoachRequest } from '../../../types';
import { LEARNING_EXPERIENCE_OPTIONS, MODALITY_PROFICIENCY_OPTIONS } from '../../../config/coachConfig';

interface EditLearningModalProps {
  opened: boolean;
  onClose: () => void;
  coach: Coach;
  onSaved: (coach: Coach) => void;
}

export function EditLearningModal({ opened, onClose, coach, onSaved }: EditLearningModalProps) {
  const form = useForm<UpdateCoachRequest>({
    initialValues: {
      modality_proficiencies: [] as string[],
      modality_approvals: [] as string[],
      preferred_learning_experiences: [],
      coaching_topics_of_interest: '',
      topics_wished_medley_addressed: '',
    },
  });

  useEffect(() => {
    if (opened && coach) {
      form.setValues({
        modality_proficiencies: coach.modality_proficiencies || [],
        modality_approvals: coach.modality_approvals || [],
        preferred_learning_experiences: coach.preferred_learning_experiences || [],
        coaching_topics_of_interest: coach.coaching_topics_of_interest || '',
        topics_wished_medley_addressed: coach.topics_wished_medley_addressed || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.setValues is stable, only re-run when modal opens with new coach data
  }, [opened, coach]);

  const handleSubmit = async (values: UpdateCoachRequest) => {
    try {
      const updatedCoach = await coachesApi.update(coach.id, values);
      notifications.show({
        title: 'Success',
        message: 'Learning preferences updated successfully',
        color: 'green',
      });
      onSaved(updatedCoach);
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to update learning preferences',
        color: 'red',
      });
    }
  };

  const toggleProficiency = (value: string) => {
    const current = form.values.modality_proficiencies || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    form.setFieldValue('modality_proficiencies', updated);
  };

  const toggleApproval = (value: string) => {
    const current = form.values.modality_approvals || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    form.setFieldValue('modality_approvals', updated);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Edit Learning & Development" size="lg">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="sm" fw={500}>Modality Proficiencies</Text>
            <Text size="xs" c="dimmed">Select modalities this coach is proficient in</Text>
          </Stack>
          <Table withRowBorders={false} verticalSpacing={6}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ fontWeight: 500, fontSize: 'var(--mantine-font-size-xs)' }}>Modality</Table.Th>
                <Table.Th style={{ fontWeight: 500, fontSize: 'var(--mantine-font-size-xs)', textAlign: 'center', width: 100 }}>Proficient</Table.Th>
                <Table.Th style={{ fontWeight: 500, fontSize: 'var(--mantine-font-size-xs)', textAlign: 'center', width: 130 }}>Medley Approved</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {MODALITY_PROFICIENCY_OPTIONS.map((option) => (
                <Table.Tr key={option.value}>
                  <Table.Td>
                    <Text size="sm">{option.label}</Text>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'center' }}>
                    <Group justify="center">
                      <Checkbox
                        checked={(form.values.modality_proficiencies || []).includes(option.value)}
                        onChange={() => toggleProficiency(option.value)}
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'center' }}>
                    <Group justify="center">
                      <Checkbox
                        checked={(form.values.modality_approvals || []).includes(option.value)}
                        onChange={() => toggleApproval(option.value)}
                        color="medleyGreen"
                      />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed" fs="italic">
            Approval selections are internal to Medley and not visible to coaches.
          </Text>
          <MultiSelect
            label="Preferred Learning Experiences"
            placeholder="Select learning preferences"
            data={LEARNING_EXPERIENCE_OPTIONS.map((l) => ({ value: l, label: l }))}
            {...form.getInputProps('preferred_learning_experiences')}
          />
          <Textarea
            label="Topics of Interest"
            placeholder="What coaching topics are you interested in learning about?"
            minRows={3}
            {...form.getInputProps('coaching_topics_of_interest')}
          />
          <Textarea
            label="Topics You Wish Medley Addressed"
            placeholder="What topics would you like Medley to cover?"
            minRows={3}
            {...form.getInputProps('topics_wished_medley_addressed')}
          />
          <Group justify="flex-end" className="modal-footer">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
